import { confirmDialogSaveText } from 'owa-locstrings/lib/strings/confirmdialogsavetext.locstring.json';
import { cancelButton as cancelButton_1 } from 'owa-locstrings/lib/strings/cancelbutton.locstring.json';
import loc from 'owa-localize';
import * as React from 'react';
import { ControlIcons } from 'owa-control-icons';
import { DefaultButton, PrimaryButton } from 'office-ui-fabric-react/lib/Button';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { observer } from 'mobx-react';
import { InlineDatePicker } from 'owa-datepicker';
import {
    owaDate,
    OwaDate,
    isSameDay,
    getYear,
    getMonth,
    getDate,
    getHours,
    getMinutes,
    getSeconds,
} from 'owa-datetime';

import TimePicker from 'owa-timepicker/lib/components/TimePicker';

const styles = require('./DateTimePicker.scss');
const classNames = require('classnames');

export interface DateTimePickerProps {
    value: OwaDate;
    minDate?: OwaDate;
    onSave?: (dateTimeSelected: OwaDate) => void;
    onCancel?: () => void;
    onChanged?: (dateTimeSelected: OwaDate) => void;
    showDuration?: boolean;
}

export interface DateTimePickerState {
    updatedDateTime: OwaDate;
}

@observer
export default class DateTimePicker extends React.Component<
    DateTimePickerProps,
    DateTimePickerState
> {
    constructor(props: DateTimePickerProps) {
        super(props);

        this.state = {
            updatedDateTime: props.value,
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps: DateTimePickerProps) {
        this.setState({
            updatedDateTime: nextProps.value,
        });
    }

    public render() {
        const { minDate, onCancel, onSave, showDuration = true } = this.props;

        const dateTimeToUse = this.state.updatedDateTime;

        return (
            <>
                <InlineDatePicker
                    value={dateTimeToUse}
                    minDate={minDate}
                    highlightCurrentMonth={false}
                    highlightSelectedMonth={true}
                    isDayPickerVisible={true}
                    showGoToToday={false}
                    onSelectDate={this.onDateSelected}
                />
                <div className={styles.timePickerContainer}>
                    <Icon className={styles.icon} iconName={ControlIcons.Clock} />
                    <TimePicker
                        className={classNames(styles.timePicker, styles.timePickerHideBorder)}
                        selectedTime={dateTimeToUse}
                        onChanged={this.onTimeSelected}
                        startTime={this.getTimePickerStartTime()}
                        showDuration={showDuration}
                    />
                </div>
                {onCancel && onSave && (
                    <div className={styles.buttonContainer}>
                        <DefaultButton
                            className={styles.cancelButton}
                            ariaLabel={loc(cancelButton_1)}
                            text={loc(cancelButton_1)}
                            onClick={onCancel}
                        />

                        <PrimaryButton
                            className={styles.saveButton}
                            ariaLabel={loc(confirmDialogSaveText)}
                            text={loc(confirmDialogSaveText)}
                            onClick={this.onSave}
                        />
                    </div>
                )}
            </>
        );
    }

    private getTimePickerStartTime(): OwaDate {
        const { minDate } = this.props;

        if (!minDate) {
            return null;
        }

        // This check is to restrict the time picker to only future times if the selected
        // date is today. Ex: Scheduling at 3pm should only show times 4pm onwards. Ex2:
        // A schedule date of tomorrow should allow for any time.
        return isSameDay(this.state.updatedDateTime, minDate) ? minDate : null;
    }

    private onSave = () => {
        this.props.onSave(this.state.updatedDateTime);
    };

    private onDateSelected = (selectedDate: OwaDate) => {
        // Change only date component. Keep the time component from previous state.
        const prevDateTime = this.state.updatedDateTime;
        const newDateTime = owaDate(
            prevDateTime.tz,
            getYear(selectedDate),
            getMonth(selectedDate),
            getDate(selectedDate),
            getHours(prevDateTime),
            getMinutes(prevDateTime),
            getSeconds(prevDateTime)
        );

        this.setState({ updatedDateTime: newDateTime });
        this.props.onChanged && this.props.onChanged(newDateTime);
    };

    private onTimeSelected = (selectedTime: OwaDate) => {
        // Change only time component. Keep the date component from previous state.
        const prevDateTime = this.state.updatedDateTime;
        const newDateTime = owaDate(
            prevDateTime.tz,
            getYear(prevDateTime),
            getMonth(prevDateTime),
            getDate(prevDateTime),
            getHours(selectedTime),
            getMinutes(selectedTime),
            getSeconds(selectedTime)
        );

        this.setState({ updatedDateTime: newDateTime });
        this.props.onChanged && this.props.onChanged(newDateTime);
    };
}
